import type {
  CreateFastInput,
  Fast,
  SessionInfo,
  Settings,
  UpdateFastInput,
  UpdateSettingsInput,
  User
} from "@if-tracking/shared";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isAuth(): boolean {
    return this.status === 401;
  }
}

export interface ImportResult {
  inserted: number;
  updated: number;
  skipped: number;
  total_rows: number;
  warnings: { row_number: number; message: string }[];
}

export type StatsRange = "this_week" | "last_7d" | "last_30d";

export interface DailyStat {
  date: string;
  total_seconds: number;
  target_seconds: number;
}

export interface StatsResponse {
  range: StatsRange;
  from_date: string;
  to_date: string;
  count: number;
  total_seconds: number;
  average_seconds: number;
  longest_seconds: number;
  target_met_count: number;
  target_met_percent: number;
  current_streak: number;
  daily: DailyStat[];
}

const BASE = "/api";

async function parseError(response: Response): Promise<ApiError> {
  let code = "ERROR";
  let message = `Request failed (${response.status})`;
  try {
    const body = (await response.json()) as { error?: string; message?: string };
    if (body.error) code = body.error;
    if (body.message) message = body.message;
  } catch {
    // non-JSON error body; keep defaults
  }
  return new ApiError(response.status, code, message);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json", ...(init.headers ?? {}) },
    ...init
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

function jsonBody(body: unknown): RequestInit {
  return {
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  };
}

export const api = {
  /** Throws ApiError(401) if the credentials are wrong, 429 when throttled. */
  async login(username: string, password: string): Promise<User> {
    const result = await request<{ user: User }>("/auth/session", {
      method: "POST",
      ...jsonBody({ username, password })
    });
    return result.user;
  },

  async logout(): Promise<void> {
    await request("/auth/logout", { method: "POST" });
  },

  /** Cheap authenticated probe used to decide whether to show the login screen. */
  async me(): Promise<User> {
    const result = await request<{ user: User }>("/me");
    return result.user;
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await request("/me/password", {
      method: "POST",
      ...jsonBody({ current_password: currentPassword, new_password: newPassword })
    });
  },

  /** Signed-in devices and browsers, newest first. */
  async listSessions(): Promise<SessionInfo[]> {
    const result = await request<{ sessions: SessionInfo[] }>("/me/sessions");
    return result.sessions;
  },

  async revokeSession(id: string): Promise<boolean> {
    const result = await request<{ deleted: boolean }>(`/me/sessions/${id}`, { method: "DELETE" });
    return result.deleted;
  },

  async getSettings(): Promise<Settings> {
    return request<Settings>("/settings");
  },

  async updateSettings(input: UpdateSettingsInput): Promise<Settings> {
    return request<Settings>("/settings", { method: "PATCH", ...jsonBody(input) });
  },

  async listFasts(params: { limit?: number; from?: number; to?: number } = {}): Promise<Fast[]> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.from !== undefined) search.set("from", String(params.from));
    if (params.to !== undefined) search.set("to", String(params.to));
    const query = search.toString();
    const result = await request<{ fasts: Fast[] }>(`/fasts${query ? `?${query}` : ""}`);
    return result.fasts;
  },

  async getStats(range: StatsRange): Promise<StatsResponse> {
    return request<StatsResponse>(`/stats?range=${range}`);
  },

  async createFast(input: CreateFastInput): Promise<Fast> {
    return request<Fast>("/fasts", { method: "POST", ...jsonBody(input) });
  },

  async updateFast(id: string, input: UpdateFastInput): Promise<Fast> {
    return request<Fast>(`/fasts/${id}`, { method: "PATCH", ...jsonBody(input) });
  },

  async deleteFast(id: string): Promise<void> {
    await request(`/fasts/${id}`, { method: "DELETE" });
  },

  async importCsv(file: File): Promise<ImportResult> {
    const form = new FormData();
    form.append("file", file, file.name);
    return request<ImportResult>("/import", { method: "POST", body: form });
  },

  exportUrl(format: "csv" | "json"): string {
    return `${BASE}/export?format=${format}`;
  }
};
